/**
 * Toast host.
 *
 * Toasts live in `state.runtime.toasts` so that any action can raise one
 * without reaching for the DOM. This component is the only thing that renders
 * them, and the only thing that owns their timers.
 *
 * The whole file is a diff: `update()` compares the incoming toast array with
 * what is already on screen and touches nothing else. That matters because
 * `update()` runs on *every* state change - a naive re-render would restart the
 * enter animation and reset the dismiss countdown of every visible toast each
 * time the manager typed a character into the rota editor.
 */

import { el } from '../dom.js';
import { icon } from '../icons.js';

/** Stable empty array so the dirty-check below never sees a fresh reference. */
const NO_TOASTS = Object.freeze([]);

/** Matches the `toast-out` animation in ui-kit.css. */
const LEAVE_MS = 200;

const TONES = {
  ok: { iconName: 'check', label: 'Success' },
  info: { iconName: 'info', label: 'Information' },
  warn: { iconName: 'warning', label: 'Warning' },
  danger: { iconName: 'warning', label: 'Error' },
};

/**
 * Build the live region that renders `state.runtime.toasts`.
 *
 * Why it takes `store` as well as `actions`: a toast can disappear from state
 * for reasons other than its own timer (a reset, a manual dismiss elsewhere).
 * Before firing `dismissToast` the host checks the toast is still there, so an
 * expiring timer for something already gone cannot trigger a pointless state
 * write and the full re-render cascade that follows it.
 *
 * @param {{ store: object, actions: object }} deps
 * @returns {{ element: HTMLElement, update: Function, destroy: Function }}
 */
export function createToastHost({ store, actions }) {
  const element = el('div', {
    class: 'toast-host',
    attrs: {
      role: 'status',
      'aria-live': 'polite',
      // role="status" implies aria-atomic="true", which would re-announce every
      // visible toast whenever one is added. Announce only what changed.
      'aria-atomic': 'false',
    },
  });

  /** @type {Map<string, { node: HTMLElement, holdTimer: number|null, leaveTimer: number|null }>} */
  const rendered = new Map();
  let lastToasts = null;

  function clearTimers(entry) {
    if (entry.holdTimer !== null) {
      clearTimeout(entry.holdTimer);
      entry.holdTimer = null;
    }
    if (entry.leaveTimer !== null) {
      clearTimeout(entry.leaveTimer);
      entry.leaveTimer = null;
    }
  }

  function requestDismiss(id) {
    const toasts = store.getState()?.runtime?.toasts ?? NO_TOASTS;
    if (toasts.some((toast) => toast.id === id)) actions.dismissToast(id);
  }

  /** Play the exit animation, then remove the toast from state. */
  function beginLeave(id) {
    const entry = rendered.get(id);
    if (!entry || entry.leaveTimer !== null) return;
    clearTimers(entry);
    entry.node.classList.add('toast--leaving');
    entry.leaveTimer = setTimeout(() => {
      entry.leaveTimer = null;
      requestDismiss(id);
    }, LEAVE_MS);
  }

  function buildToast(toast) {
    const tone = TONES[toast.tone] ? toast.tone : 'info';
    const { iconName, label } = TONES[tone];
    const message = String(toast.message ?? '');

    const dismissButton = el(
      'button',
      {
        type: 'button',
        class: 'btn btn--ghost btn--icon btn--sm',
        attrs: { 'aria-label': `Dismiss notification: ${message}`, title: 'Dismiss' },
        on: { click: () => beginLeave(toast.id) },
      },
      [icon('close', { size: 15 })],
    );

    return el(
      'div',
      { class: ['toast', `toast--${tone}`], dataset: { toastId: toast.id } },
      [
        icon(iconName, { size: 17 }),
        // The icon is decoration; screen readers get the tone as words instead.
        el('span', { class: 'sr-only', text: `${label}: ` }),
        el('span', { text: message }),
        el('span', { class: 'spacer' }),
        dismissButton,
      ],
    );
  }

  /**
   * Reconcile the rendered toasts with `state.runtime.toasts`.
   * @param {object} state
   */
  function update(state) {
    const toasts = state?.runtime?.toasts ?? NO_TOASTS;
    if (toasts === lastToasts) return;
    lastToasts = toasts;

    const live = new Set(toasts.map((toast) => toast.id));

    for (const [id, entry] of [...rendered]) {
      if (live.has(id)) continue;
      clearTimers(entry);
      entry.node.remove();
      rendered.delete(id);
    }

    for (const toast of toasts) {
      if (rendered.has(toast.id)) continue;
      const entry = { node: buildToast(toast), holdTimer: null, leaveTimer: null };
      rendered.set(toast.id, entry);
      element.appendChild(entry.node);

      const duration = Number(toast.duration);
      // A non-positive or missing duration means "stay until dismissed".
      if (Number.isFinite(duration) && duration > 0) {
        entry.holdTimer = setTimeout(() => {
          entry.holdTimer = null;
          beginLeave(toast.id);
        }, duration);
      }
    }
  }

  /** Drop every pending timer so nothing fires after the host has gone. */
  function destroy() {
    for (const entry of rendered.values()) {
      clearTimers(entry);
      entry.node.remove();
    }
    rendered.clear();
    lastToasts = null;
  }

  return { element, update, destroy };
}
